import { PropsWithChildren, createContext, useContext } from 'react';

type SSRLoginStateContextType = {
	hasCloudSessionTokenCookie: boolean;
	/**
	 * True only when the server actually resolved the session-cookie flag for this
	 * render. Lets consumers tell "SSR says there is no session" apart from "no SSR
	 * value was supplied at all" (static fallback, Storybook, tests), which need
	 * different handling: the former must be trusted to keep hydration in step,
	 * the latter has no server markup to match and can read cookies directly.
	 */
	isResolvedBySSR: boolean;
	userAgent?: string;
};

const SSRLoginStateContext = createContext<SSRLoginStateContextType>({
	hasCloudSessionTokenCookie: false,
	isResolvedBySSR: false,
});

export const useSSRLoginState = () => {
	const context = useContext(SSRLoginStateContext);
	if (!context) {
		throw new Error('useSSRLoginState must be used within SSRLoginStateProvider');
	}
	return context;
};

type SSRLoginStateProviderProps = PropsWithChildren & {
	hasCloudSessionTokenCookie?: boolean;
	userAgent?: string;
};

export const SSRLoginStateProvider = ({
	children,
	hasCloudSessionTokenCookie,
	userAgent,
}: SSRLoginStateProviderProps) => {
	return (
		<SSRLoginStateContext.Provider
			value={{
				hasCloudSessionTokenCookie: hasCloudSessionTokenCookie ?? false,
				isResolvedBySSR: hasCloudSessionTokenCookie !== undefined,
				userAgent,
			}}
		>
			{children}
		</SSRLoginStateContext.Provider>
	);
};
